import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiAlertCircleOutline } from '@mdi/js'
import { Badge } from '../ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { useCostOfOwnership, type CostOfOwnership } from '../../hooks/useCostOfOwnership'
import { SECTION_ICONS } from '../../lib/section-icons'

/**
 * Cost of ownership of one equipment (spec 2026-08-24-01, lot 2).
 *
 * The card exists to answer one question — "is this thing still worth keeping?"
 * — and to be honest about how well it can answer it. Two things are therefore
 * always visible next to the number: how the amounts reached this equipment
 * (booked on it, split from a shared invoice, or merely declared on an
 * intervention), and how much was spent on its building that could not be
 * imputed at all. A total that hides its own margin of error is worse than no
 * total.
 */

const WINDOWS = [12, 24, 36, 60]

function fmtMoney(cents: number, currency?: string): string {
  const v = cents / 100
  return currency
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(v)
    : v.toLocaleString(undefined, { maximumFractionDigits: 0 })
}

/** Ratio band. Colour is a reading aid, never the verdict. */
function ratioTone(ratio: number): { bar: string; text: string } {
  if (ratio >= 0.6) return { bar: 'bg-destructive', text: 'text-destructive' }
  if (ratio >= 0.4) return { bar: 'bg-warning', text: 'text-warning' }
  return { bar: 'bg-success', text: 'text-success' }
}

interface Props {
  org: string
  assetUid: string
  /** Rendered only on equipment — a lot has no replacement cost to compare to. */
  enabled?: boolean
}

export function CostOfOwnershipCard({ org, assetUid, enabled = true }: Props) {
  const { t } = useTranslation('interventions')
  const [windowMonths, setWindowMonths] = useState(24)
  const { data, isLoading, isError } = useCostOfOwnership(org, assetUid, { windowMonths, enabled })

  if (!enabled) return null

  if (isLoading) {
    return (
      <div
        className="rounded-lg border border-border bg-card p-4"
        data-testid="cost-of-ownership-loading"
      >
        <div className="h-4 w-40 animate-pulse rounded bg-muted" />
        <div className="mt-4 h-8 w-32 animate-pulse rounded bg-muted" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div
        className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground"
        data-testid="cost-of-ownership-error"
      >
        {t('tco.unavailable')}
      </div>
    )
  }

  return <CostOfOwnershipBody data={data} windowMonths={windowMonths} onWindowChange={setWindowMonths} />
}

function CostOfOwnershipBody({
  data,
  windowMonths,
  onWindowChange,
}: {
  data: CostOfOwnership
  windowMonths: number
  onWindowChange: (months: number) => void
}) {
  const { t } = useTranslation('interventions')
  const currency = data.currency || undefined

  const kinds = useMemo(
    () =>
      (['preventive', 'corrective', 'works'] as const)
        .map((kind) => ({ kind, amount: data.by_kind?.[kind] ?? 0 }))
        .filter((k) => k.amount > 0),
    [data.by_kind],
  )
  const maxYear = useMemo(
    () => Math.max(1, ...data.by_year.map((y) => y.amount)),
    [data.by_year],
  )

  const ratio = data.ratio_replacement ?? null
  const tone = ratio != null ? ratioTone(ratio) : null

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="cost-of-ownership-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <AppIcon tone="muted" path={SECTION_ICONS.expenses} size="md" />
          <h3 className="text-sm font-medium">{t('tco.title')}</h3>
        </div>
        <Select value={String(windowMonths)} onValueChange={(v) => onWindowChange(Number(v))}>
          <SelectTrigger className="h-8 w-[150px]" data-testid="tco-window-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOWS.map((m) => (
              <SelectItem key={m} value={String(m)} data-testid={`tco-window-${m}`}>
                {t('tco.window_months', { count: m })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-2xl font-semibold tabular-nums" data-testid="tco-total">
          {fmtMoney(data.total, currency)}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('tco.over_interventions', { count: data.intervention_count.total })}
        </span>
      </div>
      {/* What its components carry, already in the total (spec 2026-10-27-17). */}
      {data.components && data.components.count > 0 && (
        <p className="mt-1 text-xs text-muted-foreground" data-testid="tco-components">
          {t('tco.components', {
            amount: fmtMoney(data.components.amount, currency),
            count: data.components.count,
          })}
        </p>
      )}

      {ratio != null && tone && (
        <div className="mt-4" data-testid="tco-ratio">
          <div className="flex items-baseline justify-between text-xs">
            <span className="text-muted-foreground">{t('tco.ratio_label')}</span>
            <span className={`font-medium tabular-nums ${tone.text}`}>
              {Math.round(ratio * 100)} %
            </span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full rounded-full ${tone.bar}`}
              style={{ width: `${Math.min(100, Math.round(ratio * 100))}%` }}
            />
          </div>
          {data.replacement_cost_estimate != null && (
            <p className="mt-1 text-xs text-muted-foreground">
              {t('tco.replacement_cost', {
                amount: fmtMoney(Math.round(data.replacement_cost_estimate * 100), currency),
              })}
            </p>
          )}
        </div>
      )}

      {kinds.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="tco-by-kind">
          {kinds.map(({ kind, amount }) => (
            <Badge key={kind} variant="outline" className="font-normal">
              {t(`kind.${kind}`)} · {fmtMoney(amount, currency)}
            </Badge>
          ))}
        </div>
      )}

      {data.by_year.length > 1 && (
        <div className="mt-4" data-testid="tco-by-year">
          <div className="flex items-end gap-1.5" style={{ height: 48 }}>
            {data.by_year.map((y) => (
              <div key={y.year} className="flex flex-1 flex-col items-center gap-1" title={fmtMoney(y.amount, currency)}>
                <div
                  className="w-full rounded-sm bg-primary/70"
                  style={{ height: `${Math.max(2, Math.round((y.amount / maxYear) * 40))}px` }}
                />
                <span className="text-[10px] text-muted-foreground">{y.year}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* The figure's own margin of error. Both lines are deliberately plain
          text: they qualify the number above, they are not decoration. */}
      <dl className="mt-4 space-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
        <div className="flex justify-between gap-2">
          <dt>{t('tco.allocation_label')}</dt>
          <dd className="tabular-nums" data-testid="tco-allocation">
            {t('tco.allocation_value', {
              direct: fmtMoney(data.allocation.direct, currency),
              shared: fmtMoney(data.allocation.shared, currency),
              declared: fmtMoney(data.allocation.declared, currency),
            })}
          </dd>
        </div>
        {data.unallocated_on_ancestors > 0 && (
          <div className="flex items-start justify-between gap-2" data-testid="tco-unallocated">
            <dt className="flex items-center gap-1">
              <AppIcon path={mdiAlertCircleOutline} size="xs" />
              {t('tco.unallocated_label')}
            </dt>
            <dd className="tabular-nums">
              {fmtMoney(data.unallocated_on_ancestors, currency)}
            </dd>
          </div>
        )}
      </dl>
    </div>
  )
}
